/**
 * Unified Authentication Service for REAVERS
 *
 * Provides consistent Firebase authentication throughout the application,
 * replacing legacy sessionStorage-based authentication with proper
 * Firebase ID token management.
 *
 * Features:
 * - Single source of truth for authentication state
 * - Automatic token refresh and validation
 * - Unified API for all authentication operations
 * - Integration with existing WorldCommands and API systems
 * - Comprehensive error handling and recovery
 * - Legacy authentication migration support
 *
 * ⚠️ THIS CLIENT SIGNS IN. IT DOES NOT CREATE ACCOUNTS, AND IT MUST NOT.
 *
 * Account creation belongs to the Store (reavers.io) and only the Store,
 * because that is where the legally-required gates live: the 18+ attestation
 * and Terms acceptance ratified in [2026-07-17-003-STORE] row 6 / §B, captured
 * with a timestamp and version into Firestore. An account minted anywhere else
 * has no consent record and cannot be given one retroactively.
 *
 * A sibling module (`firebase-auth.js`) was removed on 2026-07-20 for exactly
 * this. It exposed `createUser()` and `signInWithGoogle()` against PRODUCTION
 * auth (`reavers-prod` — the same identity pool as the Store) with no age gate,
 * no Terms, and no consent write, and it shipped to play.reavers.io via two
 * test pages listed as Vite build inputs. It also derived an `admin` role
 * client-side from `email.includes('admin')`.
 *
 * Two rules follow, and neither is stylistic:
 *   1. Do not add account creation here. If the client ever needs it, the gate
 *      has to move or be duplicated — and social sign-in (Google et al.) CANNOT
 *      be gated by a form, because the popup provisions the Auth user before
 *      any checkbox could run. It needs an interstitial that blocks first.
 *   2. Authorization is not a client concern. Never derive a role, an
 *      entitlement, or an access level from a string the client holds. Entitlement
 *      is resolved server-side: the Store's /api/validate-purchase returns the
 *      computed `hasAccess`, which is the only answer to trust.
 *
 * How a session ARRIVES is the platform seam's (enterIdentity): the login page
 * on the web build; the Discord handshake plus a Store-minted custom token in
 * the Activity, which never navigates. Both only sign in.
 */

import { bootPlatform, enterIdentity } from './platform/platform.js';

const UnifiedAuthService = {
    // Authentication state
    state: {
        currentUser: null,
        authToken: null,
        isInitialized: false,
        isAuthenticated: false,
        authStateListeners: [],
        tokenRefreshTimer: null,
        lastTokenRefresh: null
    },

    // Configuration
    config: {
        tokenRefreshInterval: 50 * 60 * 1000, // 50 minutes (tokens expire in 60)
        sessionTimeout: 120 * 60 * 1000, // 120 minutes
        retryAttempts: 3,
        retryDelay: 1000
    },

    // Initialize the unified authentication service
    async init() {
        if (this.state.isInitialized) {
            return; // Already initialized
        }

        this.log('info', 'Initializing Unified Authentication Service');

        try {
            // Check if Firebase is available
            if (typeof firebase === 'undefined' || !firebase.auth) {
                throw new Error('Firebase Auth not available');
            }

            // Before the first firebase.auth() use: the Activity URL-maps the
            // Firebase Auth endpoints here.
            await bootPlatform();

            // Set session persistence BEFORE any auth state restoration.
            // Without this, Firebase uses IndexedDB (shared across tabs), so
            // signing in on tab2 corrupts tab1's identity. Session persistence
            // uses sessionStorage (per-tab), isolating each tab's auth state.
            try {
                const { browserSessionPersistence, setPersistence } = await import('firebase/auth');
                await setPersistence(firebase.auth(), browserSessionPersistence);
                this.log('info', 'Auth persistence set to session (tab-isolated)');
            } catch (persistErr) {
                this.log('warn', `Failed to set session persistence: ${persistErr.message}`);
            }

            // Migrate from legacy authentication if present
            await this.migrateLegacyAuth();

            // Wait for Firebase to restore auth state from sessionStorage (or timeout after 2s)
            await this.waitForAuthRestore();

            // Mark as initialized after auth state is restored
            this.state.isInitialized = true;

            // Now that auth state is restored, check if we need to redirect to login
            // This happens AFTER waitForAuthRestore, so we know the true auth state
            // How identity arrives is the platform seam's, on every init. Web: a
            // restored session is kept; with none it navigates to the login page
            // (returns false) unless the fast-path holds the page. Activity: the
            // Discord handshake runs on every launch, restored session or not.
            if (!(await enterIdentity(firebase, {
                restored: this.state.isAuthenticated,
                mayNavigate: this.shouldRedirectToLogin(),
            }))) {
                return false;
            }

            // Set up Firebase auth state listener for future changes
            this.setupFirebaseAuthListener();

            // Set up automatic token refresh
            this.setupTokenRefresh();

            // Set up auth state monitoring
            this.setupAuthStateMonitoring();

            this.log('info', 'Unified Authentication Service initialized successfully');

            return true;
        } catch (error) {
            this.log('error', `Authentication service initialization failed: ${error.message}`);
            this.handleInitializationError(error);
            return false;
        }
    },

    // Wait for Firebase to restore auth state from IndexedDB
    async waitForAuthRestore() {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                this.log('info', 'Auth restore timeout - proceeding without authenticated user');
                unsubscribe();
                resolve();
            }, 2000);

            const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
                clearTimeout(timeout);
                unsubscribe();

                if (user) {
                    this.log('info', `Auth state restored: ${user.email || user.uid}`);
                    // Update internal state
                    this.state.currentUser = user;
                    this.state.isAuthenticated = true;
                } else {
                    this.log('info', 'No authenticated user found');
                }

                resolve();
            });
        });
    },

    // Migrate from legacy sessionStorage authentication
    async migrateLegacyAuth() {
        const legacyAuth = sessionStorage.getItem('reavers_auth');

        if (legacyAuth) {
            try {
                const authData = JSON.parse(legacyAuth);

                this.log('warn', 'Legacy authentication detected - migrating to Firebase');
                this.log('info', `Legacy user: ${authData.username}`);

                // Check if session is still valid
                const loginTime = new Date(authData.loginTime);
                const sessionAge = Date.now() - loginTime.getTime();

                if (sessionAge < this.config.sessionTimeout) {
                    this.log('info', 'Legacy session still valid - preserving user context');
                    // Store user context for post-Firebase-auth restoration
                    this.storeLegacyContext(authData);
                } else {
                    this.log('warn', 'Legacy session expired - requiring fresh authentication');
                }

                // Remove legacy authentication
                sessionStorage.removeItem('reavers_auth');
                this.log('info', 'Legacy authentication removed');

            } catch (error) {
                this.log('error', `Legacy auth migration error: ${error.message}`);
                sessionStorage.removeItem('reavers_auth'); // Remove corrupted data
            }
        }
    },

    // Store legacy context for restoration
    storeLegacyContext(authData) {
        const context = {
            username: authData.username,
            loginTime: authData.loginTime,
            migrated: true,
            migrationTime: new Date().toISOString()
        };

        // Store in sessionStorage with a different key
        sessionStorage.setItem('reavers_migration_context', JSON.stringify(context));
    },

    // Restore legacy context after Firebase auth
    restoreLegacyContext() {
        const context = sessionStorage.getItem('reavers_migration_context');
        if (context) {
            try {
                const contextData = JSON.parse(context);
                this.log('info', `Restored user context: ${contextData.username}`);

                // Update status display
                this.updateStatusDisplay(contextData.username);

                // Clean up migration context
                sessionStorage.removeItem('reavers_migration_context');

                return contextData;
            } catch (error) {
                this.log('error', `Context restoration error: ${error.message}`);
                sessionStorage.removeItem('reavers_migration_context');
            }
        }
        return null;
    },

    // Setup Firebase authentication state listener
    setupFirebaseAuthListener() {
        firebase.auth().onAuthStateChanged(async (user) => {
            this.log('info', `Auth state changed: ${user ? 'signed in' : 'signed out'}`);

            this.state.currentUser = user;
            this.state.isAuthenticated = !!user;

            if (user) {
                try {
                    // Get fresh ID token
                    const token = await user.getIdToken();
                    this.state.authToken = token;
                    this.state.lastTokenRefresh = Date.now();

                    this.log('info', `Authentication successful: ${user.email || user.uid}`);

                    // Try to restore legacy context
                    const legacyContext = this.restoreLegacyContext();

                    // Update status display
                    this.updateStatusDisplay(legacyContext?.username || user.email || 'User');

                    // Notify listeners
                    this.notifyAuthStateListeners(user, token);

                } catch (error) {
                    this.log('error', `Token retrieval error: ${error.message}`);
                    this.handleAuthError(error);
                }
            } else {
                // User signed out
                this.state.authToken = null;
                this.state.lastTokenRefresh = null;

                // Clear status display
                this.updateStatusDisplay(null);

                // Notify listeners
                this.notifyAuthStateListeners(null, null);

                // Handle unauthenticated state
                this.handleUnauthenticatedState();
            }
        });
    },

    // Setup automatic token refresh
    setupTokenRefresh() {
        // Clear existing timer
        if (this.state.tokenRefreshTimer) {
            clearInterval(this.state.tokenRefreshTimer);
        }

        // Set up periodic token refresh
        this.state.tokenRefreshTimer = setInterval(async () => {
            if (this.state.currentUser && this.state.isAuthenticated) {
                try {
                    await this.refreshToken();
                } catch (error) {
                    this.log('error', `Automatic token refresh failed: ${error.message}`);
                }
            }
        }, this.config.tokenRefreshInterval);
    },

    // Setup authentication state monitoring
    setupAuthStateMonitoring() {
        // Monitor for auth errors
        window.addEventListener('beforeunload', () => {
            this.cleanup();
        });

        // Integrate with diagnostic system if available
        if (typeof GameDiagnostics !== 'undefined') {
            // Call integration directly since GameDiagnostics is already loaded
            this.integrateWithDiagnostics();
        }
    },

    // Refresh authentication token
    async refreshToken(forceRefresh = false) {
        if (!this.state.currentUser) {
            throw new Error('No authenticated user for token refresh');
        }

        try {
            const token = await this.state.currentUser.getIdToken(forceRefresh);
            this.state.authToken = token;
            this.state.lastTokenRefresh = Date.now();

            this.log('info', 'Authentication token refreshed successfully');

            // Notify listeners of token update
            this.notifyAuthStateListeners(this.state.currentUser, token);

            return token;
        } catch (error) {
            this.log('error', `Token refresh failed: ${error.message}`);
            throw error;
        }
    },

    // Get current authentication headers for API calls
    async getAuthHeaders() {
        if (!this.state.isAuthenticated || !this.state.currentUser) {
            throw new Error('User not authenticated');
        }

        try {
            // Check if token needs refresh
            const tokenAge = Date.now() - (this.state.lastTokenRefresh || 0);
            const shouldRefresh = tokenAge > this.config.tokenRefreshInterval;

            let token = this.state.authToken;

            if (shouldRefresh || !token) {
                token = await this.refreshToken();
            }

            return {
                // Firebase ID token -- verified server-side by BOTH the GameServer (WS)
                // and the WMS HTTP API (auth-unify WS-A). `Authorization: Bearer <idToken>`
                // is the FIXED header name the backend verifies; the WMS gate decodes the
                // verified uid/email from the token claims (no client-asserted identity in prod).
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Auth-Provider': 'firebase',
                // Emulator-mode fallback only. In prod the backend ignores these and trusts
                // the verified token; kept so the local emulator + VP harness keep working.
                'X-Player-ID': this.state.currentUser.uid,
                'X-Player-Email': this.state.currentUser.email || ''
            };
        } catch (error) {
            this.log('error', `Failed to get auth headers: ${error.message}`);
            throw new Error('Authentication headers unavailable');
        }
    },

    // Check if user is authenticated
    isAuthenticated() {
        return this.state.isAuthenticated && !!this.state.currentUser && !!this.state.authToken;
    },

    // Get current user information
    getCurrentUser() {
        return this.state.currentUser;
    },

    // Get current auth token
    getCurrentToken() {
        return this.state.authToken;
    },

    // Sign in with email and password
    async signIn(email, password) {
        try {
            this.log('info', `Attempting sign in for: ${email}`);

            const credential = await firebase.auth().signInWithEmailAndPassword(email, password);

            this.log('info', 'Sign in successful');
            return { success: true, user: credential.user };

        } catch (error) {
            this.log('error', `Sign in failed: ${error.message}`);
            return { success: false, error: error.message, code: error.code };
        }
    },

    // Sign out
    async signOut() {
        try {
            this.log('info', 'Signing out user');

            await firebase.auth().signOut();

            // Clean up state
            this.cleanup();

            this.log('info', 'Sign out successful');
            return { success: true };

        } catch (error) {
            this.log('error', `Sign out failed: ${error.message}`);
            return { success: false, error: error.message };
        }
    },

    // Register authentication state listener
    onAuthStateChanged(callback) {
        this.state.authStateListeners.push(callback);

        // Call immediately if already authenticated
        if (this.state.isAuthenticated) {
            callback(this.state.currentUser, this.state.authToken);
        }
    },

    // Remove authentication state listener
    removeAuthStateListener(callback) {
        const index = this.state.authStateListeners.indexOf(callback);
        if (index > -1) {
            this.state.authStateListeners.splice(index, 1);
        }
    },

    // Notify all auth state listeners
    notifyAuthStateListeners(user, token) {
        for (const listener of this.state.authStateListeners) {
            try {
                listener(user, token);
            } catch (error) {
                this.log('error', `Auth state listener error: ${error.message}`);
            }
        }
    },

    // Update status display in UI
    updateStatusDisplay(username) {
        const statusElement = document.getElementById('status');
        if (statusElement) {
            if (username) {
                statusElement.textContent = `READY [${username.toUpperCase()}]`;
            } else {
                statusElement.textContent = 'READY';
            }
        }
    },

    // Handle unauthenticated state
    handleUnauthenticatedState() {
        this.log('warn', 'User not authenticated');

        // Don't do anything during initialization - auth might still be restoring
        if (!this.state.isInitialized) {
            this.log('info', 'Still initializing - skipping unauthenticated handling');
            return;
        }

        // After initialization is complete, if not authenticated, notify listeners first
        this.log('info', 'No authenticated user - notifying listeners');
        this.notifyAuthStateListeners(null, null);

        // Then redirect to login if on a protected page
        if (this.shouldRedirectToLogin()) {
            this.log('warn', 'Entering identity');
            enterIdentity(firebase).catch((error) => {
                this.log('error', `Identity entry failed: ${(error && error.stack) || String(error)}`);
                this.handleInitializationError(error);
            });
        }
    },

    // Check if should redirect to login
    shouldRedirectToLogin() {
        // [vp-thin-client-fastpath] When the fast-path is active (?fastpath=1),
        // never navigate to the login form. This is the 0-authnav fix: the
        // fast-path page stays put and waits for in-place JS-auth (the VP
        // harness / a persisted dev session) instead of racing a login-form
        // navigation. Flag is set synchronously by main.js before init().
        if (typeof window !== 'undefined' && window.__REAVERS_FASTPATH_SUPPRESS_LOGIN__) {
            this.log('info', 'Fast-path active - suppressing login redirect (awaiting in-place JS-auth)');
            return false;
        }
        // Only redirect if we're not already on a test page or auth page
        const path = window.location.pathname;
        return !path.includes('test') && !path.includes('login') && !path.includes('auth');
    },

    // Show authentication prompt
    showAuthenticationPrompt() {
        // Create authentication prompt overlay
        const overlay = document.createElement('div');
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.9);
            z-index: 10000;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Courier New', monospace;
            color: white;
        `;

        overlay.innerHTML = `
            <div style="
                background: #1a0000;
                border: 2px solid #ff0000;
                padding: 30px;
                border-radius: 8px;
                text-align: center;
                max-width: 400px;
            ">
                <h2 style="color: #ff0000; margin: 0 0 20px 0;">🔐 Authentication Required</h2>
                <p style="margin: 0 0 20px 0;">
                    You need to sign in to access the REAVERS game.
                </p>
                <div style="margin: 20px 0;">
                    <input type="email" id="auth-email" placeholder="Email" style="
                        width: 100%;
                        padding: 10px;
                        margin: 5px 0;
                        background: #000;
                        border: 1px solid #666;
                        color: white;
                        font-family: inherit;
                    ">
                    <input type="password" id="auth-password" placeholder="Password" style="
                        width: 100%;
                        padding: 10px;
                        margin: 5px 0;
                        background: #000;
                        border: 1px solid #666;
                        color: white;
                        font-family: inherit;
                    ">
                </div>
                <button onclick="UnifiedAuthService.promptSignIn()" style="
                    background: #ff0000;
                    border: none;
                    color: white;
                    padding: 12px 24px;
                    margin: 5px;
                    cursor: pointer;
                    font-family: inherit;
                    border-radius: 4px;
                ">Sign In</button>
                <button onclick="window.location.reload()" style="
                    background: #666;
                    border: none;
                    color: white;
                    padding: 12px 24px;
                    margin: 5px;
                    cursor: pointer;
                    font-family: inherit;
                    border-radius: 4px;
                ">Reload</button>
            </div>
        `;

        document.body.appendChild(overlay);
    },

    // Handle sign in from prompt
    async promptSignIn() {
        const email = document.getElementById('auth-email')?.value;
        const password = document.getElementById('auth-password')?.value;

        if (!email || !password) {
            alert('Please enter both email and password');
            return;
        }

        try {
            const result = await this.signIn(email, password);
            if (result.success) {
                // Remove auth prompt
                document.querySelector('.auth-overlay')?.remove();
                // Reload page to restart with authentication
                window.location.reload();
            } else {
                alert(`Sign in failed: ${result.error}`);
            }
        } catch (error) {
            alert(`Sign in error: ${error.message}`);
        }
    },

    // Handle authentication errors
    handleAuthError(error) {
        this.log('error', `Authentication error: ${error.message}`);

        // Integrate with diagnostic system if available
        if (typeof GameDiagnostics !== 'undefined') {
            GameDiagnostics.handleError({
                component: 'unified_auth',
                category: 'authentication',
                severity: 'high',
                message: error.message,
                details: {
                    errorCode: error.code,
                    stack: error.stack
                }
            });
        }
    },

    // Handle initialization errors
    handleInitializationError(error) {
        this.log('error', `Initialization error: ${error.message}`);

        // Show user-friendly error
        if (typeof GameDiagnostics !== 'undefined') {
            GameDiagnostics.handleError({
                component: 'unified_auth',
                category: 'initialization',
                severity: 'critical',
                message: 'Authentication system failed to initialize',
                details: { error: error.message }
            });
        }
    },

    // Integrate with diagnostic system
    integrateWithDiagnostics() {
        // Add auth status to system diagnostics
        if (typeof GameDiagnostics !== 'undefined' && typeof GameDiagnostics.addCustomCheck === 'function') {
            GameDiagnostics.addCustomCheck('authentication', () => {
                return {
                    isInitialized: this.state.isInitialized,
                    isAuthenticated: this.state.isAuthenticated,
                    hasToken: !!this.state.authToken,
                    tokenAge: this.state.lastTokenRefresh ? Date.now() - this.state.lastTokenRefresh : null,
                    userId: this.state.currentUser?.uid || null,
                    legacyAuthRemoved: !sessionStorage.getItem('reavers_auth')
                };
            });
        }
    },

    // Get authentication status for diagnostics
    getAuthStatus() {
        return {
            isInitialized: this.state.isInitialized,
            isAuthenticated: this.state.isAuthenticated,
            currentUser: this.state.currentUser ? {
                uid: this.state.currentUser.uid,
                email: this.state.currentUser.email,
                emailVerified: this.state.currentUser.emailVerified
            } : null,
            hasToken: !!this.state.authToken,
            tokenAge: this.state.lastTokenRefresh ? Date.now() - this.state.lastTokenRefresh : null,
            legacyAuthPresent: !!sessionStorage.getItem('reavers_auth'),
            migrationContextPresent: !!sessionStorage.getItem('reavers_migration_context')
        };
    },

    // Clean up resources
    cleanup() {
        if (this.state.tokenRefreshTimer) {
            clearInterval(this.state.tokenRefreshTimer);
            this.state.tokenRefreshTimer = null;
        }

        // Clear sensitive data
        this.state.authToken = null;
        this.state.lastTokenRefresh = null;
    },

    // Logging method
    log(level, message, details = null) {
        const logMessage = `[UnifiedAuth] ${message}`;
        console[level](logMessage, details || '');

        // Also log to GameDiagnostics if available
        if (typeof GameDiagnostics !== 'undefined' && GameDiagnostics.log) {
            GameDiagnostics.log(level, logMessage, details);
        }
    }
};

// ES Module export
export default UnifiedAuthService;